import { Component, inject, InjectionToken } from '@angular/core';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { LoadOptions } from 'devextreme/data/load_options';
import { firstValueFrom, map, Observable, of, shareReplay, tap } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import AppSettings from 'src/app/common/app.settings';
import AppValues from 'src/app/common/app.values';
import ProjectLinks from 'src/app/common/project.links';
import { ActivitiesService } from 'src/app/components/activities/activities.service';
import { loadOptionsToParams } from 'src/app/components/files/files-manager.helpers';
import { createLink } from 'src/app/components/profile/user-profile/view-profile/regular-user-profile/regular-user-profile-helper';
import { DialogActivitiesData, MessageLevel, Session } from 'src/app/interfaces';
import { FilesActivityLogView } from 'src/app/interfaces/activity.interface';
import { ActivityLogEvent, FilePermissions, UserBasic } from 'src/app/swagger-api';
import { NotificationState } from '../../notifications.state';
import { DialogActionTypes } from '../dialogs.enum';

export const DIALOG_ACTIVITIES_DATA = new InjectionToken<DialogActivitiesData>(`DIALOG_ACTIVITIES_DATA`);

@Component({
  selector: 'app-dialog-activities',
  templateUrl: './dialog-activities.component.html',
  styleUrls: ['./dialog-activities.component.scss'],
})
export class DialogActivitiesComponent extends NotificationState {
  public readonly ACTIVITIES_PAGE_LIMIT: number = AppValues.PAGE_LIMIT;
  public readonly ACTIVITIES_PAGE_OFFSET: number = AppValues.ACTIVITIES_PAGE_OFFSET;
  public readonly ACTIVITIES_FILES_UPLOADED_EVENT: ActivityLogEvent.EventTypeEnum =
    ActivityLogEvent.EventTypeEnum.FilesUploadedEvent;
  public readonly ACTIVITIES_FILES_SHARED: ActivityLogEvent.EventTypeEnum = ActivityLogEvent.EventTypeEnum.FileShared;
  public readonly ACTIVITIES_FILES_DOWNLOADED: ActivityLogEvent.EventTypeEnum =
    ActivityLogEvent.EventTypeEnum.FileDownloaded;
  public readonly ACTIVITIES_FILES_UNSHARED: ActivityLogEvent.EventTypeEnum =
    ActivityLogEvent.EventTypeEnum.FileUnshared;
  public readonly ACTIVITIES_FILES_PERMISSION: ActivityLogEvent.EventTypeEnum =
    ActivityLogEvent.EventTypeEnum.FilePermissionUpdated;

  public readonly NAVIGATE_TO_PROFILE: string = ProjectLinks.LINK_PROFILE_VIEW;
  public readonly OWNER: string = FilePermissions.Owner;
  public readonly PERMISSION_READER: string = AppValues.PERMISSION_READER;
  public readonly PERMISSION_OWNER: string = AppValues.PERMISSION_OWNER;

  public totalCount = 0;
  public totalDownloadCount = 0;

  protected notificationLevel: MessageLevel = 'INFO';

  public AppSettings: typeof AppSettings = AppSettings;

  private activitiesService = inject(ActivitiesService);

  public readonly dialog = inject(DIALOG_ACTIVITIES_DATA);
  public readonly hasUnsavedChanges$: Observable<boolean> = of(false);

  public readonly activeUserId$: Observable<string> = inject(SessionService).sessionChanges.pipe(
    shareReplay(1),
    map((session: Session) => session.userId),
  );

  public readonly activitiesDataSource: DataSource = new DataSource({
    store: new CustomStore({
      key: 'event_id',
      load: async (loadOptions: LoadOptions) =>
        firstValueFrom(
          this.activitiesService.showIndividualFileActivities(this.dialog.items, loadOptionsToParams(loadOptions)).pipe(
            tap((response: FilesActivityLogView) => {
              this.totalCount = response.total_count;
              this.totalDownloadCount = response.total_download_count;
            }),
          ),
        ).then((response: FilesActivityLogView) => ({ data: response.items, totalCount: response.total_count })),
    }),
  });

  public actionsHandler(action: DialogActionTypes): void {
    this.dialogActions[action]();
  }

  public submit(): void {
    this.activeModal.close();
  }

  public getLink(user: UserBasic): string {
    return createLink(user.account_status, user.user_id);
  }
}
